import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import TitleContainer from '../TitleContainer';
import SettingsOptionsAndInterfaceMode from './settingsOptions/SettingsOptionsAndInterfaceMode';
import AllSettingsSelectOptions from './settingsOptions/AllSettingsSelectOptions';
import TitleOfSettingsOptions from './TitleOfSettingsOptions';
import EditCancelApplyButtons from './buttons/EditCancelApplyButtons';
import UserProfileMain from './userProfile/UserProfileMain';
import UnitsMain from './units/UnitsMain';
import WindFactorMain from './windFactorTrigger/WindFactorMain';
import SnowSensorMain from './snowSensorSensor/SnowSensorMain';
import ForceAndCommandMain from './ForceAndCommand/ForceAndCommandMain';
import AdminMain from './admin/AdminMain';
import Interface from './interfaceMode/Interface';
import {
  handleSetInitialStateSettingsOptions,
  selectSettingsOptions,
} from '../store/slices/settings/settingsOptionsSlice';
import {
  handleClickedButton,
  handleResetButtons,
} from '../store/slices/settings/editCancelApplyButtonsSlice';
import { SETTINGS_HEADERS } from './settingsHeaders';
import { settingsMessage } from './hooks/settingsMessage';
import { useSettingsMessage } from './hooks/useSettingsMessage';
import { useSaveUserProfile } from './hooks/useSaveUserProfile';
import { useSaveUnits } from './hooks/useSaveUnits';
import { useSaveWindFactor } from './hooks/useSaveWindFactor';
import { useSaveSnowSensor } from './hooks/useSaveSnowSensor';
import { useSaveForceAndCommands } from './hooks/useSaveForceAndCommands';
import { useSaveSystemIdentification } from './hooks/useSaveSystemIdentification';
import { useSaveAdmin } from './hooks/useSaveAdmin';
import {
  ButtonsWrapper,
  FlexColumn,
  MainSectionBaseLayer,
  MainSectionWrapper,
  Wrapper,
  WrapperSetting,
  WrapperSettingsModeAndSelect,
  WrapperTitle,
} from './settingsMainStyles';

// The buttons' ids in EditCancelApplyButtons.
const EDIT = 0;
const CANCEL = 1;
const APPLY = 2;

const closedHeaders = () => [false, false, false, false, false, false];

/**
 * The settings page: one panel at a time, picked in the settings options.
 * Each panel's state and saves live in its hook (`./hooks`). Panels call
 * `handleEditCancelApplyButtons(button, header)`; on force & commands and
 * admin, `header` is the row (`SETTINGS_HEADERS`) the buttons sit in.
 */
const SettingsMain = ({ essRefetch, tgsRefetch, tesRefetch }) => {
  const isMobile = useMediaQuery({ query: '(max-width:600px)' });
  const dispatch = useDispatch();
  const {
    isUserProfileSelected,
    isUnitsSelected,
    isWindFactorSelected,
    isSnowSensorSelected,
    isForceAndCommandsSelected,
    isAdminSelected,
    isInterfaceModeSelected,
  } = useSelector(selectSettingsOptions).allSettingsOptions;

  const [openHeaders, setOpenHeaders] = useState(closedHeaders);
  const message = useSettingsMessage();

  const refetchAllSystems = () => {
    essRefetch();
    tgsRefetch();
    tesRefetch();
  };

  const profile = useSaveUserProfile();
  const units = useSaveUnits();
  const windFactor = useSaveWindFactor();
  const snowSensor = useSaveSnowSensor();
  const forceAndCommands = useSaveForceAndCommands();
  const systemIdentification = useSaveSystemIdentification({
    openHeaders,
    refetchAllSystems,
    showMessage: message.show,
  });
  const admin = useSaveAdmin({
    saveSystemIdentification: systemIdentification.save,
  });

  useEffect(() => {
    setOpenHeaders(closedHeaders());
  }, [isForceAndCommandsSelected, isAdminSelected]);

  useEffect(() => {
    dispatch(handleSetInitialStateSettingsOptions());
    return () => dispatch(handleResetButtons());
  }, []);

  const cancelPanel = (header) => {
    if (isUserProfileSelected) {
      profile.cancel();
    }
    if (isForceAndCommandsSelected) {
      forceAndCommands.cancel(header);
    }
    if (isAdminSelected && header === SETTINGS_HEADERS.SYSTEM_COMMANDS) {
      systemIdentification.cancel();
    }
  };

  const applyPanel = async (header) => {
    if (isUserProfileSelected) {
      message.show(await profile.save());
      return;
    }

    dispatch(handleResetButtons());
    dispatch(handleClickedButton('isApply'));
    if (isUnitsSelected) {
      message.show(await units.save());
    } else if (isWindFactorSelected) {
      message.show(windFactor.review());
    } else if (isSnowSensorSelected) {
      message.show(snowSensor.review());
    } else if (isForceAndCommandsSelected) {
      message.show(await forceAndCommands.save(header));
    } else if (isAdminSelected) {
      const { content, keepOpen } = await admin.save(header);
      message.show(content, { keepOpen });
    } else {
      message.show(settingsMessage('', '', '', ''));
    }
  };

  const handleEditCancelApplyButtons = async (button, header) => {
    switch (Number(button)) {
      case EDIT:
        dispatch(handleResetButtons());
        dispatch(handleClickedButton('isEdit'));
        break;
      case CANCEL:
        dispatch(handleResetButtons());
        dispatch(handleClickedButton('isCancel'));
        cancelPanel(header);
        break;
      case APPLY:
        await applyPanel(header);
        break;
      default:
    }
  };

  const handleCloseMessageBox = () => {
    if (message.close()) {
      return;
    }
    if (isForceAndCommandsSelected || isAdminSelected) {
      setOpenHeaders(closedHeaders());
    }
  };

  // The wind and snow confirm boxes: send the reviewed triggers. A failed
  // send comes back as a message, shown in the same box (confirm retries).
  const handleConfirmButton = async (settingOption) => {
    handleCloseMessageBox();
    const confirmSave = {
      windFactor: windFactor.confirm,
      snowSensor: snowSensor.confirm,
    }[settingOption];
    const failure = confirmSave ? await confirmSave() : null;
    if (failure) {
      message.show(failure);
    }
  };

  const messageProps = {
    messageBoxContent: message.content,
    openMessageBox: message.isOpen,
    handleCloseMessageBox,
  };
  const headerProps = { openHeaders, setOpenHeaders };

  const buttons = (wrapperProps) => (
    <ButtonsWrapper {...wrapperProps}>
      <EditCancelApplyButtons handleClick={handleEditCancelApplyButtons} />
    </ButtonsWrapper>
  );
  const onPhone = isMobile ? { isMobile: true } : {};

  const renderPanel = () => {
    if (isUserProfileSelected) {
      return (
        <FlexColumn>
          <UserProfileMain {...profile.panelProps} {...messageProps} />
          {buttons(isMobile ? { isMobile: true } : { isUserProfile: true })}
        </FlexColumn>
      );
    }
    if (isUnitsSelected) {
      return (
        <WrapperSetting>
          <UnitsMain
            handleClick={handleEditCancelApplyButtons}
            {...units.panelProps}
            {...messageProps}
          />
        </WrapperSetting>
      );
    }
    if (isWindFactorSelected) {
      return (
        <FlexColumn isSpaceBetween={true}>
          <WindFactorMain
            {...windFactor.panelProps}
            {...messageProps}
            handleConfirmButton={handleConfirmButton}
          />
          {buttons(onPhone)}
        </FlexColumn>
      );
    }
    if (isSnowSensorSelected) {
      return (
        <FlexColumn
          {...(isMobile
            ? { isSmallSpaceBetween: true }
            : { isSpaceBetween: true })}
        >
          <SnowSensorMain
            {...snowSensor.panelProps}
            {...messageProps}
            handleConfirmButton={handleConfirmButton}
          />
          {buttons(onPhone)}
        </FlexColumn>
      );
    }
    if (isForceAndCommandsSelected) {
      return (
        <WrapperSetting>
          <ForceAndCommandMain
            handleClick={handleEditCancelApplyButtons}
            {...forceAndCommands.panelProps}
            {...headerProps}
            {...messageProps}
          />
        </WrapperSetting>
      );
    }
    if (isAdminSelected) {
      return (
        <WrapperSetting>
          <AdminMain
            handleClick={handleEditCancelApplyButtons}
            {...admin.panelProps}
            {...systemIdentification.panelProps}
            {...headerProps}
            {...messageProps}
          />
        </WrapperSetting>
      );
    }
    if (isMobile && isInterfaceModeSelected) {
      return (
        <WrapperSetting>
          <Interface />
        </WrapperSetting>
      );
    }
    return null;
  };

  if (isMobile) {
    return (
      <MainSectionBaseLayer isMobile={true}>
        <MainSectionWrapper isMobile={true}>
          <WrapperTitle isMobile={true}>
            <TitleContainer title='settings' />
          </WrapperTitle>
          <AllSettingsSelectOptions />
          {renderPanel()}
        </MainSectionWrapper>
      </MainSectionBaseLayer>
    );
  }

  return (
    <Wrapper>
      <TitleContainer title='settings' />
      <MainSectionBaseLayer>
        <MainSectionWrapper>
          <WrapperTitle>
            <TitleOfSettingsOptions />
          </WrapperTitle>
          <WrapperSettingsModeAndSelect>
            <SettingsOptionsAndInterfaceMode />
            {renderPanel()}
          </WrapperSettingsModeAndSelect>
        </MainSectionWrapper>
      </MainSectionBaseLayer>
    </Wrapper>
  );
};

export default SettingsMain;
